import React, { useState, useRef, useEffect } from 'react';
import { Trash2, Building2, SquarePen, Sparkles } from 'lucide-react';
import { api } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import { showToast } from '../../../utils/toastManager';
import '../../../styles/resume-editor.css';

interface Experience {
  uid: string;
  employer_name: string;
  job_title: string;
  start_date: string | null;
  end_date: string | null;
  is_current: boolean;
  location?: string;
  description?: string;
  matchScore?: number;
  achievements?: string[];
  skills?: string[];
}

interface ExperienceCardProps {
  experience: Experience;
  onEdit: (experience: Experience) => void;
  onDelete: (uid: string) => void;
  onReorderAchievements?: (uid: string, newOrder: string[]) => void;
  cvUid?: string;
  onRewriteAchievementWithAI?: (cvUid: string, experienceUid: string, achievementText: string) => void;
  isVolunteer?: boolean;
}

export const ExperienceCard: React.FC<ExperienceCardProps> = ({ experience, onEdit, onDelete, onReorderAchievements, cvUid, onRewriteAchievementWithAI, isVolunteer = false }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [hoveredAchievementIndex, setHoveredAchievementIndex] = useState<number | null>(null);
  const [hoveredActionIndex, setHoveredActionIndex] = useState<{ achievementIndex: number; actionIndex: number } | null>(null);
  const [editingAchievementIndex, setEditingAchievementIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState<string>('');
  const menuRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${month} '${year}`;
  };

  const formatDateRange = (): string => {
    const startDate = formatDate(experience.start_date);
    const endDate = experience.is_current ? 'Now' : formatDate(experience.end_date);

    if (startDate && endDate) {
      return `${startDate} to ${endDate}`;
    }
    if (startDate) {
      return `from ${startDate}`;
    }
    if (endDate) {
      return `upto ${endDate}`;
    }
    return '';
  };

  const dateRange = formatDateRange();

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (draggedIndex === null || draggedIndex === dropIndex || !experience.achievements) {
      setDraggedIndex(null);
      return;
    }

    const newAchievements = [...experience.achievements];
    const [removed] = newAchievements.splice(draggedIndex, 1);
    newAchievements.splice(dropIndex, 0, removed);

    if (onReorderAchievements) {
      onReorderAchievements(experience.uid, newAchievements);
    }

    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (editingAchievementIndex !== null && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
    }
  }, [editingAchievementIndex]);

  const handleEditAchievement = (index: number) => {
    if (experience.achievements && experience.achievements[index]) {
      setEditingText(experience.achievements[index]);
      setEditingAchievementIndex(index);
      setHoveredAchievementIndex(null);
    }
  };

  const handleSaveAchievement = (index: number) => {
    if (experience.achievements && onReorderAchievements) {
      const trimmedText = editingText.trim();
      if (trimmedText === '') {
        const newAchievements = experience.achievements.filter((_, i) => i !== index);
        onReorderAchievements(experience.uid, newAchievements);
      } else {
        const newAchievements = [...experience.achievements];
        newAchievements[index] = trimmedText;
        onReorderAchievements(experience.uid, newAchievements);
      }
    }
    setEditingAchievementIndex(null);
    setEditingText('');
  };

  const handleCancelEdit = () => {
    if (editingAchievementIndex !== null && experience.achievements) {
      const achievement = experience.achievements[editingAchievementIndex];
      if (achievement === '' && onReorderAchievements) {
        const newAchievements = experience.achievements.filter((_, i) => i !== editingAchievementIndex);
        onReorderAchievements(experience.uid, newAchievements);
      }
    }
    setEditingAchievementIndex(null);
    setEditingText('');
  };

  const handleAddAchievement = () => {
    if (experience.achievements && onReorderAchievements) {
      const newAchievements = [...experience.achievements, ''];
      onReorderAchievements(experience.uid, newAchievements);
      const newIndex = newAchievements.length - 1;
      setEditingText('');
      setEditingAchievementIndex(newIndex);
    }
  };

  return (
    <div className="resume-editor__experience-card">
      <div className="resume-editor__experience-card-header">
        <div className="resume-editor__experience-card-info">
          <h3 className="resume-editor__experience-card-title">{experience.job_title}</h3>
          <div className="resume-editor__experience-card-company-row">
            <div className="resume-editor__experience-card-company">
              <div className="resume-editor__experience-card-logo">
                <Building2 size={16} />
              </div>
              <p className="resume-editor__experience-card-subtitle">{experience.employer_name}</p>
            </div>
          </div>
        </div>
        <div className="resume-editor__experience-card-menu-container" ref={menuRef}>
          <button 
            className="resume-editor__experience-card-menu" 
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ⋮
          </button>
          <p className="resume-editor__experience-card-date">{dateRange}</p>
          {menuOpen && (
            <div className="resume-editor__experience-card-dropdown">
              <button
                className="resume-editor__experience-card-dropdown-item"
                onClick={() => {
                  onEdit(experience);
                  setMenuOpen(false);
                }}
              >
                Edit experience
              </button>
              <button
                className="resume-editor__experience-card-dropdown-item resume-editor__experience-card-dropdown-item--delete"
                onClick={() => {
                  onDelete(experience.uid);
                  setMenuOpen(false);
                }}
              >
                Delete experience
              </button>
            </div>
          )}
        </div>
      </div>

      {experience.description && (
        <p className="resume-editor__experience-card-company-description">{experience.description}</p>
      )}

      {experience.matchScore !== undefined && (
        <div className="resume-editor__experience-card-score">
          <div className="resume-editor__experience-card-score-badge">
            {experience.matchScore}
          </div>
          <span className="resume-editor__experience-card-score-text">Match score for target job title</span>
        </div>
      )}

      {experience.achievements && experience.achievements.length > 0 && (
        <div className="resume-editor__experience-card-body">
          <div className="resume-editor__experience-card-achievements">
            {experience.achievements.map((achievement, index) => (
              editingAchievementIndex === index ? (
                <div
                  key={index}
                  className="resume-editor__experience-card-achievement-tile resume-editor__experience-card-achievement-tile--editing"
                >
                  <textarea
                    ref={textareaRef}
                    className="resume-editor__experience-card-achievement-textarea"
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSaveAchievement(index);
                      }
                      if (e.key === 'Escape') {
                        handleCancelEdit();
                      }
                    }}
                    rows={2}
                  />
                  <div className="resume-editor__experience-card-achievement-edit-actions">
                    <button
                      className="resume-editor__experience-card-achievement-save-btn"
                      onClick={() => handleSaveAchievement(index)}
                    >
                      Save
                    </button>
                    <button
                      className="resume-editor__experience-card-achievement-cancel-btn"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  key={index}
                  className={`resume-editor__experience-card-achievement-tile ${
                    draggedIndex === index ? 'resume-editor__experience-card-achievement-tile--dragging' : ''
                  } ${
                    dragOverIndex === index ? 'resume-editor__experience-card-achievement-tile--drag-over' : ''
                  }`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  onMouseEnter={() => setHoveredAchievementIndex(index)}
                  onMouseLeave={() => setHoveredAchievementIndex(null)}
                  onClick={() => handleEditAchievement(index)}
                >
                  <span className="resume-editor__experience-card-achievement-text">{achievement}</span>
                  {hoveredAchievementIndex === index && (
                    <div className="resume-editor__experience-card-achievement-actions">
                      <div
                        className="resume-editor__experience-card-achievement-action-wrapper"
                        onMouseEnter={() => setHoveredActionIndex({ achievementIndex: index, actionIndex: 0 })}
                        onMouseLeave={() => setHoveredActionIndex(null)}
                      >
                        <button
                          className="resume-editor__experience-card-achievement-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditAchievement(index);
                          }}
                        >
                          <SquarePen size={14} />
                        </button>
                        {hoveredActionIndex?.achievementIndex === index && hoveredActionIndex?.actionIndex === 0 && (
                          <span className="resume-editor__experience-card-achievement-tooltip">Edit</span>
                        )}
                      </div>
                      <div
                        className="resume-editor__experience-card-achievement-action-wrapper"
                        onMouseEnter={() => setHoveredActionIndex({ achievementIndex: index, actionIndex: 1 })}
                        onMouseLeave={() => setHoveredActionIndex(null)}
                      >
                        <button
                          className="resume-editor__experience-card-achievement-action"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (cvUid && onRewriteAchievementWithAI) {
                              onRewriteAchievementWithAI(cvUid, experience.uid, achievement);
                            } else if (cvUid) {
                              try {
                                await api.post(API_ENDPOINTS.aiCv.createTask, {
                                  cv_uid: cvUid,
                                  entity_type: isVolunteer ? 'volunteer_experience_achievement' : 'work_experience_achievement',
                                  entity_uid: experience.uid,
                                  improvement_type: 'current',
                                  old_value: { value: [achievement] },
                                });
                                if ((window as any).__refreshAITasks) {
                                  (window as any).__refreshAITasks();
                                }
                              } catch (error: any) {
                                console.error('Failed to create AI CV task for achievement:', error);
                                const errorMessage = error?.data?.detail || error?.message || 'Failed to create AI task. Please try again.';
                                showToast(errorMessage, 'error');
                              }
                            }
                          }}
                        >
                          <Sparkles size={14} />
                        </button>
                        {hoveredActionIndex?.achievementIndex === index && hoveredActionIndex?.actionIndex === 1 && (
                          <span className="resume-editor__experience-card-achievement-tooltip">Rewrite with AI</span>
                        )}
                      </div>
                      <div
                        className="resume-editor__experience-card-achievement-action-wrapper"
                        onMouseEnter={() => setHoveredActionIndex({ achievementIndex: index, actionIndex: 2 })}
                        onMouseLeave={() => setHoveredActionIndex(null)}
                      >
                        <button
                          className="resume-editor__experience-card-achievement-action resume-editor__experience-card-achievement-action--delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (experience.achievements) {
                              const newAchievements = experience.achievements.filter((_, i) => i !== index);
                              if (onReorderAchievements) {
                                onReorderAchievements(experience.uid, newAchievements);
                              }
                            }
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                        {hoveredActionIndex?.achievementIndex === index && hoveredActionIndex?.actionIndex === 2 && (
                          <span className="resume-editor__experience-card-achievement-tooltip">Remove</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            ))}
          </div>
          <div className="resume-editor__experience-card-actions">
            <button 
              className="resume-editor__add-achievement-btn"
              onClick={handleAddAchievement}
            >
              + Achievement
            </button>
            <button 
              className="resume-editor__ai-suggestions-btn"
              onClick={async (e) => {
                e.stopPropagation();
                if (cvUid) {
                  const hasAchievements = Array.isArray(experience.achievements) && experience.achievements.length > 0;
                  try {
                    const payload: any = {
                      cv_uid: cvUid,
                      entity_type: isVolunteer ? 'volunteer_experience_block' : 'work_experience_block',
                      entity_uid: experience.uid,
                      improvement_type: hasAchievements ? 'current' : 'new',
                    };

                    if (hasAchievements) {
                      payload.old_value = { value: experience.achievements };
                    }

                    await api.post(API_ENDPOINTS.aiCv.createTask, payload);
                    if ((window as any).__refreshAITasks) {
                      (window as any).__refreshAITasks();
                    }
                  } catch (error: any) {
                    console.error('Failed to create AI CV task for experience:', error);
                    const errorMessage = error?.data?.detail || error?.message || 'Failed to create AI task. Please try again.';
                    showToast(errorMessage, 'error');
                  }
                }
              }}
            >
              <Sparkles size={14} />
              <span>AI Suggestions</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

